import {ReactElement, ReactNode} from "react";
import {Popover} from "@headlessui/react";
import {IconLabel} from "components/UIComponents/IconLabel";
import {FormattedMessage} from "react-intl";
import {menuEditorActions, menuEditorSelectors} from "./editorSlice";
import {useAppDispatch, useAppSelector} from "../../lib/redux/hooks";
import {createEmptyWine, createImage, createMargin, createPageBreak, createPageTitle, createTitle} from "./editorUtils";
import {ReactComponent as WineIcon} from "components/Icons/wine.svg";
import {ReactComponent as PlusIcon} from "components/Icons/plusEmpty.svg";
import {ReactComponent as TitleIconSmall} from 'components/Icons/titleSmall.svg';
import {ReactComponent as TitleIconBig} from 'components/Icons/titleBig.svg';
import {ReactComponent as MarginIcon} from 'components/Icons/enlarge.svg';
import {ReactComponent as ImageIcon} from 'components/Icons/image.svg';
import {ReactComponent as PageBreakIcon} from 'components/Icons/page.svg';
import {MenuItem} from "./MenuDocumentModel";
import {SecondaryButton} from "../../components/UIComponents/SecondaryButton";


export interface AddDropDownProps {

}

export function AddDropDown(props: AddDropDownProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);

    const dispatch = useAppDispatch();
    const currentFocus = useAppSelector(menuEditorSelectors.focusedItem);

    const addItem = (creator: () => MenuItem) => dispatch(menuEditorActions.addItems({
        after: currentFocus?.itemKey,
        items: [creator()]
    }));
    const openWinePicker = () => dispatch(menuEditorActions.setWinePickerActive(true));

    return (
        <Popover as="div" className="relative">
            <Popover.Button as="div">
                <SecondaryButton>
                    <IconLabel icon={<PlusIcon className="h-5 w-5"/>}>
                        <FormattedMessage id="editor.nav.add"/>
                    </IconLabel>
                </SecondaryButton>
            </Popover.Button>

            <Popover.Panel className="absolute mt-3 transform -translate-x-1/2 left-1/2
                                      drop-shadow-dropdown-menu z-popover">

                {/* arrow */}
                <div className="w-3 h-3 bg-white z-popover-arrow
                                transform rotate-45
                                absolute -top-1.5 -translate-x-1/2 left-1/2"/>

                {/* actual popover panel */}
                <div className="overflow-hidden z-popover rounded-lg bg-white
                                font-barlow text-base pt-4 pb-3 pl-3 pr-6">
                    <Group>
                        <GroupHeader>
                            <FormattedMessage id="editor.nav.group.wine"/>
                        </GroupHeader>

                        <AddEntry onClick={openWinePicker} icon={<WineIcon />}>
                            <FormattedMessage id="editor.nav.add.pickWine"/>
                        </AddEntry>

                        <AddEntry onClick={() => addItem(createEmptyWine)} icon={<PlusIcon />}>
                            <FormattedMessage id="editor.nav.add.addWine"/>
                        </AddEntry>
                    </Group>

                    {document&&document.type==='wine'&&<Group>
                        <GroupHeader>
                            <FormattedMessage id="editor.nav.group.layout" />
                        </GroupHeader>

                        <AddEntry onClick={() => addItem(createTitle)} icon={<TitleIconSmall/>}>
                            <FormattedMessage id="editor.nav.add.addTitle"/>
                        </AddEntry>

                        <AddEntry onClick={() => addItem(createPageTitle)} icon={<TitleIconBig />}>
                            <FormattedMessage id="editor.nav.add.addPageTitle"/>
                        </AddEntry>

                        <AddEntry onClick={() => addItem(createMargin)} icon={<MarginIcon />}>
                            <FormattedMessage id="editor.nav.add.addMargin"/>
                        </AddEntry>

                        <AddEntry onClick={() => addItem(createImage)} icon={<ImageIcon />}>
                            <FormattedMessage id="editor.nav.add.addImage"/>
                        </AddEntry>

                        <AddEntry onClick={() => addItem(createPageBreak)} icon={<PageBreakIcon />}>
                            <FormattedMessage id="editor.nav.add.pageBreak"/>
                        </AddEntry>

                    </Group>}
                </div>
            </Popover.Panel>
        </Popover>
    );
}


interface GroupProps {
    children: ReactNode[]
}

function Group(props: GroupProps): ReactElement {
    return (
        <div className="flex flex-col items-stretch gap-2 mb-3 last:mb-0">
            {props.children}
        </div>
    );
}

interface GroupHeaderProps {
    children: ReactNode
}

function GroupHeader(props: GroupHeaderProps): ReactElement {
    return (
        <h3 className="text-fira-sans text-xs uppercase leading-snug text-text">
            {props.children}
        </h3>
    );
}


interface AddEntryProps {
    onClick: () => void
    icon: ReactNode
    children: ReactNode
}

function AddEntry(props: AddEntryProps): ReactElement {
    return (
        <Popover.Button as="div" className="whitespace-nowrap">
            <button className="py-2 w-full hover:bg-warm-gray
                           border-b border-b-gray-3 last:border-b-0"
                    onClick={props.onClick}>
                <IconLabel icon={<span className="text-secondary">{props.icon}</span>}>
                    <span className="pl-1">{props.children}</span>
                </IconLabel>
            </button>
        </Popover.Button>
    );
}